/**
 * Sketch kernel — turns SketchEntity[] into closed ProfileRegions.
 *
 * Pipeline:
 *  1. Tessellate each entity into 2D polylines (circles/rects are already
 *     closed loops; lines/arcs are open chains).
 *  2. Chain open polylines end-to-end (quantized endpoint matching) into
 *     closed loops; unclosed chains are ignored for profiles (they'll still
 *     matter later as construction/sweep-path geometry).
 *  3. Nest loops by containment: even depth = region boundary, odd depth =
 *     hole in its immediate parent. An island inside a hole becomes its own
 *     region — same rule Fusion/Onshape apply to sketch profiles.
 *
 * Everything is computed in sketch UV space; `planeToWorld` places the
 * result in 3D (column-major 4x4: columns = u, v, normal, origin).
 */

import type { ProfileRegion } from './types';
import type { SketchEntity, SketchPlane, Vec2 } from '../timeline/types';

export type Loop = Vec2[];

const CHAIN_EPS = 1e-4;

export interface PlaneBasis {
  origin: readonly [number, number, number];
  u: readonly [number, number, number];
  v: readonly [number, number, number];
  normal: readonly [number, number, number];
}

export function planeBasis(plane: SketchPlane): PlaneBasis {
  if (plane.kind === 'face' || plane.kind === 'bface') {
    // Face refs resolve through evaluation (against the body's geometry).
    throw new Error('Face-attached planes resolve at evaluation time');
  }
  if (plane.kind === 'free') {
    // Arbitrary planar face: derive a stable U/V from the normal.
    const nLen = Math.hypot(...plane.normal) || 1;
    const n: [number, number, number] = [
      plane.normal[0] / nLen,
      plane.normal[1] / nLen,
      plane.normal[2] / nLen,
    ];
    const seed: [number, number, number] = Math.abs(n[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
    const ux = seed[1] * n[2] - seed[2] * n[1];
    const uy = seed[2] * n[0] - seed[0] * n[2];
    const uz = seed[0] * n[1] - seed[1] * n[0];
    const uLen = Math.hypot(ux, uy, uz) || 1;
    const u: [number, number, number] = [ux / uLen, uy / uLen, uz / uLen];
    const v: [number, number, number] = [
      n[1] * u[2] - n[2] * u[1],
      n[2] * u[0] - n[0] * u[2],
      n[0] * u[1] - n[1] * u[0],
    ];
    return { origin: plane.origin, u, v, normal: n };
  }
  const o = plane.offset;
  switch (plane.plane) {
    case 'XY':
      return { origin: [0, 0, o], u: [1, 0, 0], v: [0, 1, 0], normal: [0, 0, 1] };
    case 'YZ':
      return { origin: [o, 0, 0], u: [0, 1, 0], v: [0, 0, 1], normal: [1, 0, 0] };
    case 'XZ':
      // u×v must equal the normal: (1,0,0)×(0,0,1) = (0,-1,0).
      return { origin: [0, o, 0], u: [1, 0, 0], v: [0, 0, 1], normal: [0, -1, 0] };
  }
}

export function planeToWorldMatrix(basis: PlaneBasis): Float32Array {
  const { u, v, normal: n, origin: o } = basis;
  // Column-major 4x4.
  return new Float32Array([
    u[0], u[1], u[2], 0,
    v[0], v[1], v[2], 0,
    n[0], n[1], n[2], 0,
    o[0], o[1], o[2], 1,
  ]);
}

export function uvToWorld(m: Float32Array, p: Vec2): [number, number, number] {
  return [
    m[0]! * p[0] + m[4]! * p[1] + m[12]!,
    m[1]! * p[0] + m[5]! * p[1] + m[13]!,
    m[2]! * p[0] + m[6]! * p[1] + m[14]!,
  ];
}

/** Adaptive segment count for a full circle of the given radius. */
function circleSegments(radius: number): number {
  return Math.max(24, Math.min(128, Math.ceil(radius * 1.5)));
}

/**
 * Sample a fair curve THROUGH the given points: centripetal Catmull-Rom.
 * This is the display/profile approximation of the exact B-spline the
 * B-rep kernel interpolates through the same points — both pass through
 * every point, so profiles, snapping, and previews line up.
 */
export function sampleSpline(
  points: readonly Vec2[],
  closed: boolean,
  segsPerSpan = 12,
): Vec2[] {
  const n = points.length;
  if (n < 2) return [...points] as Vec2[];
  if (n === 2 && !closed) return [...points] as Vec2[];

  const pt = (i: number): Vec2 => {
    if (closed) return points[((i % n) + n) % n]!;
    // Clamped ends: mirror-pad so the curve starts/ends AT the endpoints.
    if (i < 0) return points[0]!;
    if (i >= n) return points[n - 1]!;
    return points[i]!;
  };
  const out: Vec2[] = [];
  const spans = closed ? n : n - 1;
  for (let s = 0; s < spans; s++) {
    const p0 = pt(s - 1), p1 = pt(s), p2 = pt(s + 1), p3 = pt(s + 2);
    // Centripetal parameterization (α = 0.5) avoids loops/overshoot.
    const t01 = Math.sqrt(Math.hypot(p1[0] - p0[0], p1[1] - p0[1])) || 1e-6;
    const t12 = Math.sqrt(Math.hypot(p2[0] - p1[0], p2[1] - p1[1])) || 1e-6;
    const t23 = Math.sqrt(Math.hypot(p3[0] - p2[0], p3[1] - p2[1])) || 1e-6;
    // Tangents (Barry–Goldman form).
    const m1: Vec2 = [
      (p2[0] - p1[0] + t12 * ((p1[0] - p0[0]) / t01 - (p2[0] - p0[0]) / (t01 + t12))),
      (p2[1] - p1[1] + t12 * ((p1[1] - p0[1]) / t01 - (p2[1] - p0[1]) / (t01 + t12))),
    ];
    const m2: Vec2 = [
      (p2[0] - p1[0] + t12 * ((p3[0] - p2[0]) / t23 - (p3[0] - p1[0]) / (t12 + t23))),
      (p2[1] - p1[1] + t12 * ((p3[1] - p2[1]) / t23 - (p3[1] - p1[1]) / (t12 + t23))),
    ];
    for (let k = 0; k < segsPerSpan; k++) {
      const t = k / segsPerSpan;
      const t2 = t * t, t3 = t2 * t;
      const h00 = 2 * t3 - 3 * t2 + 1, h10 = t3 - 2 * t2 + t;
      const h01 = -2 * t3 + 3 * t2, h11 = t3 - t2;
      out.push([
        h00 * p1[0] + h10 * m1[0] + h01 * p2[0] + h11 * m2[0],
        h00 * p1[1] + h10 * m1[1] + h01 * p2[1] + h11 * m2[1],
      ]);
    }
  }
  if (!closed) out.push(points[n - 1]!);
  return out;
}

function tessellateEntity(e: SketchEntity): { points: Loop; closed: boolean } {
  switch (e.kind) {
    case 'line':
      return { points: [e.a, e.b], closed: false };
    case 'rect': {
      const [x0, y0] = e.min;
      const [x1, y1] = e.max;
      return {
        points: [
          [x0, y0],
          [x1, y0],
          [x1, y1],
          [x0, y1],
        ],
        closed: true,
      };
    }
    case 'circle': {
      const n = circleSegments(e.radius);
      const pts: Loop = [];
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        pts.push([
          e.center[0] + e.radius * Math.cos(a),
          e.center[1] + e.radius * Math.sin(a),
        ]);
      }
      return { points: pts, closed: true };
    }
    case 'ellipse': {
      const n = circleSegments(Math.max(e.rx, e.ry));
      const pts: Loop = [];
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        pts.push([e.center[0] + e.rx * Math.cos(a), e.center[1] + e.ry * Math.sin(a)]);
      }
      return { points: pts, closed: true };
    }
    case 'arc': {
      let span = e.endAngle - e.startAngle;
      if (span <= 0) span += Math.PI * 2;
      const n = Math.max(2, Math.ceil((circleSegments(e.radius) * span) / (Math.PI * 2)));
      const pts: Loop = [];
      for (let i = 0; i <= n; i++) {
        const a = e.startAngle + (span * i) / n;
        pts.push([
          e.center[0] + e.radius * Math.cos(a),
          e.center[1] + e.radius * Math.sin(a),
        ]);
      }
      return { points: pts, closed: false };
    }
    case 'spline':
      return {
        points: sampleSpline(e.points, !!e.closed) as Loop,
        closed: !!e.closed,
      };
    case 'poly':
      return { points: e.points as Loop, closed: true };
    case 'text':
      // Text must be expanded to 'poly' loops (core/text) before kernels.
      throw new Error('Text entity reached the kernel unexpanded');
  }
}

const keyOf = (p: Vec2): string =>
  `${Math.round(p[0] / CHAIN_EPS)}:${Math.round(p[1] / CHAIN_EPS)}`;

const samePoint = (a: Vec2, b: Vec2): boolean =>
  Math.abs(a[0] - b[0]) < CHAIN_EPS && Math.abs(a[1] - b[1]) < CHAIN_EPS;

/**
 * Chain open polylines into closed loops by matching endpoints. Greedy walk:
 * start from any unused chain, keep appending whichever unused chain starts
 * (or ends, reversed) at the current endpoint, until the loop closes.
 */
export function chainIntoLoops(open: Loop[]): { loops: Loop[]; unclosed: Loop[] } {
  const loops: Loop[] = [];
  const unclosed: Loop[] = [];
  const used = new Set<number>();

  // Endpoint index: key → list of {chain, atStart}
  const index = new Map<string, { i: number; atStart: boolean }[]>();
  open.forEach((chain, i) => {
    for (const [p, atStart] of [
      [chain[0]!, true],
      [chain[chain.length - 1]!, false],
    ] as const) {
      const k = keyOf(p);
      if (!index.has(k)) index.set(k, []);
      index.get(k)!.push({ i, atStart });
    }
  });

  for (let start = 0; start < open.length; start++) {
    if (used.has(start)) continue;
    used.add(start);
    const path: Loop = [...open[start]!];

    for (;;) {
      const tail = path[path.length - 1]!;
      if (path.length > 2 && samePoint(tail, path[0]!)) {
        path.pop(); // drop duplicated closing point
        loops.push(path);
        break;
      }
      const candidates = (index.get(keyOf(tail)) ?? []).filter((c) => !used.has(c.i));
      const next = candidates[0];
      if (!next) {
        unclosed.push(path);
        break;
      }
      used.add(next.i);
      const chain = open[next.i]!;
      const append = next.atStart ? chain.slice(1) : chain.slice(0, -1).reverse();
      path.push(...append);
    }
  }
  return { loops, unclosed };
}

export function signedArea(loop: Loop): number {
  let area = 0;
  for (let i = 0; i < loop.length; i++) {
    const [x0, y0] = loop[i]!;
    const [x1, y1] = loop[(i + 1) % loop.length]!;
    area += x0 * y1 - x1 * y0;
  }
  return area / 2;
}

function pointInLoop(p: Vec2, loop: Loop): boolean {
  let inside = false;
  for (let i = 0, j = loop.length - 1; i < loop.length; j = i++) {
    const [xi, yi] = loop[i]!;
    const [xj, yj] = loop[j]!;
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

const ensureCCW = (loop: Loop): Loop => (signedArea(loop) < 0 ? [...loop].reverse() : loop);
const ensureCW = (loop: Loop): Loop => (signedArea(loop) > 0 ? [...loop].reverse() : loop);

/**
 * SketchUp-style 2-point arc: endpoints A/B plus a signed bulge (sagitta —
 * perpendicular distance from the chord midpoint to the arc; positive =
 * left of A→B). Returns the equivalent center/radius arc entity, or null
 * when degenerate (coincident points or ~zero bulge).
 */
export function arcFromTwoPoints(
  a: Vec2,
  b: Vec2,
  bulge: number,
): Extract<SketchEntity, { kind: 'arc' }> | null {
  const chord = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (chord < 1e-9 || Math.abs(bulge) < 1e-9) return null;
  const radius = (chord * chord * 0.25 + bulge * bulge) / (2 * Math.abs(bulge));
  // Unit perpendicular on the LEFT of A→B.
  const nx = -(b[1] - a[1]) / chord;
  const ny = (b[0] - a[0]) / chord;
  const mx = (a[0] + b[0]) / 2;
  const my = (a[1] + b[1]) / 2;
  const center: Vec2 = [
    mx + (bulge - Math.sign(bulge) * radius) * nx,
    my + (bulge - Math.sign(bulge) * radius) * ny,
  ];
  const angA = Math.atan2(a[1] - center[1], a[0] - center[0]);
  const angB = Math.atan2(b[1] - center[1], b[0] - center[0]);
  // Our arcs tessellate CCW start→end. A left bulge is reached going CCW
  // from B to A; a right bulge going CCW from A to B.
  return bulge > 0
    ? { kind: 'arc', center, radius, startAngle: angB, endAngle: angA }
    : { kind: 'arc', center, radius, startAngle: angA, endAngle: angB };
}

/** Translate a plane matrix `w` along its own normal (face-cap planes). */
export function offsetPlaneMatrix(m: Float32Array, w: number): Float32Array {
  const out = new Float32Array(m);
  out[12] = m[12]! + m[8]! * w;
  out[13] = m[13]! + m[9]! * w;
  out[14] = m[14]! + m[10]! * w;
  return out;
}

/**
 * Build profile regions from sketch entities on a datum plane. Regions are
 * sorted by descending area so `profile:0` is the biggest — a stable pick.
 */
export function buildProfileRegions(
  entities: readonly SketchEntity[],
  plane: SketchPlane,
): ProfileRegion[] {
  return buildSketchShapes(entities, planeToWorldMatrix(planeBasis(plane))).profiles;
}

/**
 * Full sketch geometry against a resolved plane matrix: closed regions
 * (nested, oriented) plus open chains (sweep paths, construction curves).
 */
export function buildSketchShapes(
  entities: readonly SketchEntity[],
  matrix: Float32Array,
): { profiles: ProfileRegion[]; paths: Loop[] } {
  const closed: Loop[] = [];
  const open: Loop[] = [];
  for (const e of entities) {
    const { points, closed: isClosed } = tessellateEntity(e);
    (isClosed ? closed : open).push(points);
  }
  const chained = chainIntoLoops(open);
  const loops = [...closed, ...chained.loops].filter((l) => Math.abs(signedArea(l)) > 1e-9);

  // Containment depth + immediate parent (smallest-area container).
  const areas = loops.map((l) => Math.abs(signedArea(l)));
  const parents: (number | null)[] = loops.map((loop, i) => {
    let best: number | null = null;
    for (let j = 0; j < loops.length; j++) {
      if (i === j || areas[j]! <= areas[i]!) continue;
      if (pointInLoop(loop[0]!, loops[j]!)) {
        if (best === null || areas[j]! < areas[best]!) best = j;
      }
    }
    return best;
  });
  const depth = (i: number): number => {
    let d = 0;
    for (let p = parents[i]; p !== null && p !== undefined; p = parents[p]) d++;
    return d;
  };

  const regions: { outer: Loop; holes: Loop[]; area: number }[] = [];
  const regionIndexByLoop = new Map<number, number>();

  loops.forEach((loop, i) => {
    if (depth(i) % 2 === 0) {
      regionIndexByLoop.set(i, regions.length);
      regions.push({ outer: ensureCCW(loop), holes: [], area: areas[i]! });
    }
  });
  loops.forEach((loop, i) => {
    if (depth(i) % 2 === 1) {
      const parentRegion = regionIndexByLoop.get(parents[i]!);
      if (parentRegion !== undefined) regions[parentRegion]!.holes.push(ensureCW(loop));
    }
  });

  return {
    profiles: regions
      .sort((a, b) => b.area - a.area)
      .map((r) => ({ outer: r.outer, holes: r.holes, planeToWorld: matrix })),
    paths: chained.unclosed,
  };
}
